import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';

import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { Label } from 'core/components/label';
import { Tag } from 'core/components/tag';
import { TextField } from 'core/components/text-field';
import { LabelTooltip } from 'core/components/label-tooltip';
import Checkbox from 'core/components/checkbox';
import Switch from 'core/components/switch';
import { formatResultCount } from '../search-link';
import { ExpandedSearchTooltips } from '../search-form/search-form';
import { ALL_RESULTS_ROUTE, visibleSearchTypes } from '../../search-types';
import useStyles from './condensed-search-header.styles';

const OLDER_OFFER_VERSIONS_TOOLTIP = 'Shows every saved version of each offer, so you can go back to an earlier one.';

/**
 * The condensed layout's Search header: title, a full-width search field, record-type chips with their counts, and
 * the search options. Classic and the new look without the condensed layout keep SearchHeader + SearchForm.
 */
const CondensedSearchHeader = observer(function CondensedSearchHeader({
  value,
  onChange,
  onSearchButtonPress,
  location
}) {
  const classes = useStyles();
  const session = useSession();
  const { search: store } = useStore();
  const { pathname } = location;
  const types = visibleSearchTypes(session);
  const chips = [
    {
      key: 'all',
      label: 'All',
      to: ALL_RESULTS_ROUTE,
      count: types.reduce((total, type) => total + (store[type.resultsKey]?.nbHits ?? 0), 0)
    },
    ...types.map((type) => ({ ...type, count: store[type.resultsKey]?.nbHits ?? 0 }))
  ];
  const onAll = pathname === ALL_RESULTS_ROUTE;
  const showExpandedSearch = onAll || ['/search/offers', '/search/customers', '/search/branch-plus'].includes(pathname);

  const toggleExpandedSearch = useCallback(
    (_, expanded) => {
      store.setExpandedSearch(expanded);
      onSearchButtonPress();
    },
    [store, onSearchButtonPress]
  );

  const toggleShowOfferHistory = useCallback(
    (_, show) => {
      store.setShowOfferHistory(show);
      onSearchButtonPress();
    },
    [store, onSearchButtonPress]
  );

  const onSubmit = (event) => {
    event.preventDefault();
    onSearchButtonPress();
  };

  return (
    <div className={classes.header}>
      <Label type="title" className={classes.title}>
        Search
      </Label>
      <p className={classes.description}>Find customers, policies, and offers by name, address, or ID.</p>
      <form role="search" className={classes.form} onSubmit={onSubmit}>
        <TextField
          name="query"
          className={classes.field}
          autoComplete="off"
          placeholder="Search by name, address, or ID"
          value={value}
          onChange={onChange}
          inputProps={{ 'aria-label': 'Search records' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={classes.searchIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  data-cy="submit-search"
                >
                  Search
                </Button>
              </InputAdornment>
            )
          }}
        />
      </form>
      <div className={classes.tools}>
        <nav className={classes.chips} aria-label="Record type">
          {chips.map((chip) => {
            const selected = pathname === chip.to;
            return (
              <Link
                key={chip.key}
                to={chip.to}
                className={classNames(classes.chip, { [classes.selectedChip]: selected })}
                aria-current={selected ? 'page' : undefined}
                data-cy={chip.key}
              >
                {chip.label}
                {chip.count > 0 && <span className={classes.chipCount}>{formatResultCount(chip.count)}</span>}
                {chip.internal && (
                  <Tag variant="internal" className={classes.internalTag}>
                    Internal
                  </Tag>
                )}
              </Link>
            );
          })}
        </nav>
        <div className={classes.options}>
          {(onAll || pathname === '/search/offers') && (
            <Switch
              id="toggleShowOfferHistory"
              label="Show older offer versions"
              tooltipText={OLDER_OFFER_VERSIONS_TOOLTIP}
              tooltipLabel="More info"
              onChange={toggleShowOfferHistory}
              value={store.showOfferHistory}
            />
          )}
          {showExpandedSearch && (
            <Checkbox
              id="toggleExpandedSearch"
              label="Expanded Search"
              tooltipText={
                pathname === '/search/branch-plus'
                  ? ExpandedSearchTooltips.branchPlus
                  : ExpandedSearchTooltips.customers
              }
              tooltipLabel="More Info"
              onChange={toggleExpandedSearch}
              value={store.expandedSearch}
            />
          )}
          {pathname === '/search/policies' && (
            <LabelTooltip
              tooltip={{ onHoverText: ExpandedSearchTooltips.policies, label: 'What can I search?' }}
              placement="bottom"
            />
          )}
        </div>
      </div>
    </div>
  );
});

CondensedSearchHeader.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSearchButtonPress: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired
};

export default CondensedSearchHeader;
